// shell/components/entity-fields/form-renderers/tag-input.ts

import { createElement, clearElement } from '../../../utils/dom';
import { debounce } from '../../../utils/dom';
import type { TagInputFormField, SelectOption } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a tag input with optional suggestions.
 */
export function renderTagInput<T>(
    field: TagInputFormField<T>,
    initialValue: unknown,
    onChange: (value: string[]) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--tag-input',
    });

    // Keep the tag list alphabetically ordered when the field opts in, so it stays
    // sorted as the user adds tags (not just on first render). Applied at every
    // assignment below (init, add, setValue). Case-insensitive to match how the
    // same values read in list views.
    const orderTags = (arr: string[]): string[] =>
        field.sorted ? [...arr].sort((a, b) => a.localeCompare(b)) : arr;

    // Current tags
    let tags: string[] = [];
    if (Array.isArray(initialValue)) {
        tags = initialValue.map(String);
    } else if (typeof initialValue === 'string') {
        tags = initialValue.split(',').map(s => s.trim()).filter(Boolean);
    }
    tags = orderTags(tags);

    // Tags container
    const tagsContainer = createElement('div', {
        className: field.vertical
            ? 'ev-tags-container ev-tags-container--vertical'
            : 'ev-tags-container',
    });

    // Input element
    const inputEl = createElement('input', {
        className: 'ev-input ev-tag-input',
        attributes: {
            type: 'text',
            placeholder: field.placeholder ?? 'Add tag...',
            ...(field.disabled ? { disabled: '' } : {}),
        },
    }) as HTMLInputElement;

    // Suggestions dropdown
    const dropdown = createElement('div', {
        className: 'ev-tag-dropdown',
    });
    dropdown.style.display = 'none';

    // State
    let suggestions: SelectOption[] = [];
    let highlightIndex = -1;
    let abortController: AbortController | null = null;

    // Render tags
    const renderTags = () => {
        clearElement(tagsContainer);

        for (const tag of tags) {
            const tagEl = createElement('span', {
                className: 'ev-tag',
            });
            const label = createElement('span', {
                className: 'ev-tag-label',
                textContent: tag,
            });
            tagEl.appendChild(label);

            if (!field.disabled) {
                const removeBtn = createElement('button', {
                    className: 'ev-tag-remove',
                    textContent: '×',
                    attributes: { type: 'button', 'aria-label': `Remove ${tag}` },
                    onClick: (e) => {
                        e.stopPropagation();
                        removeTag(tag);
                    },
                });
                tagEl.appendChild(removeBtn);
            }
            tagsContainer.appendChild(tagEl);
        }

        // Append input after tags
        tagsContainer.appendChild(inputEl);

        // Check max tags
        if (field.maxTags && tags.length >= field.maxTags) {
            inputEl.style.display = 'none';
        } else {
            inputEl.style.display = '';
        }
    };

    // Add tag
    const addTag = (tag: string) => {
        const trimmed = tag.trim();
        if (!trimmed) return;
        if (tags.includes(trimmed)) return;
        if (field.maxTags && tags.length >= field.maxTags) return;

        tags = orderTags([...tags, trimmed]);
        renderTags();
        onChange(tags);
        inputEl.value = '';
        closeDropdown();
    };

    // Remove tag
    const removeTag = (tag: string) => {
        tags = tags.filter(t => t !== tag);
        renderTags();
        onChange(tags);
    };

    // Commit whatever the user has typed but not yet turned into a tag. Without
    // this, text left in the input on save is silently dropped: the form reads
    // the committed `tags`, never this buffer. Returns true if a tag was added.
    const commitPending = (): boolean => {
        if (field.allowCustom === false) return false;
        if (!inputEl.value.trim()) return false;
        addTag(inputEl.value);
        return true;
    };

    // Fetch suggestions
    const fetchSuggestions = async (query: string) => {
        if (!field.fetchSuggestions) return;

        abortController?.abort();
        abortController = new AbortController();

        try {
            suggestions = await field.fetchSuggestions(query);
            renderDropdown();
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                suggestions = [];
                renderDropdown();
            }
        }
    };

    const debouncedFetch = debounce((query: unknown) => fetchSuggestions(query as string), 300);

    // Render dropdown
    const renderDropdown = () => {
        clearElement(dropdown);
        highlightIndex = -1;

        const filtered = suggestions.filter(s => !tags.includes(s.key));

        if (filtered.length === 0) {
            dropdown.style.display = 'none';
            return;
        }

        dropdown.style.display = 'block';

        filtered.forEach((suggestion, index) => {
            const item = createElement('div', {
                className: 'ev-tag-suggestion',
                textContent: suggestion.label,
                attributes: { 'data-index': String(index) },
                onClick: () => addTag(suggestion.key),
            });
            // Keep focus in the input so picking a suggestion doesn't fire the
            // input's blur (which would commit the raw text alongside the pick).
            item.addEventListener('mousedown', (ev) => ev.preventDefault());
            dropdown.appendChild(item);
        });
    };

    const closeDropdown = () => {
        dropdown.style.display = 'none';
        suggestions = [];
        highlightIndex = -1;
    };

    // Keyboard handling
    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightIndex >= 0 && suggestions[highlightIndex]) {
                addTag(suggestions[highlightIndex].key);
            } else {
                commitPending();
            }
        } else if (e.key === 'Backspace' && inputEl.value === '' && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
        } else if (e.key === 'ArrowDown' && suggestions.length > 0) {
            e.preventDefault();
            highlightIndex = Math.min(highlightIndex + 1, suggestions.length - 1);
            updateHighlight();
        } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
            e.preventDefault();
            highlightIndex = Math.max(highlightIndex - 1, 0);
            updateHighlight();
        } else if (e.key === 'Escape') {
            closeDropdown();
        } else if (e.key === ',') {
            e.preventDefault();
            commitPending();
        }
    };

    const updateHighlight = () => {
        const items = dropdown.querySelectorAll('.ev-tag-suggestion');
        items.forEach((item, i) => {
            item.classList.toggle('ev-tag-suggestion--highlighted', i === highlightIndex);
        });
    };

    // Handle input
    const handleInput = () => {
        if (field.fetchSuggestions) {
            debouncedFetch(inputEl.value);
        }
    };

    const handleBlur = (e: FocusEvent) => {
        // Focus moving to another control inside this widget (the remove ×, etc.)
        // is not "leaving" — don't commit, let that interaction run. Suggestion
        // items aren't focusable; their mousedown is prevent-defaulted below so
        // clicking one never blurs the input (and so never steals its raw text).
        const next = e.relatedTarget as Node | null;
        if (next && container.contains(next)) return;

        // Leaving for real (another field, the Save button, the page): commit
        // any pending text so it reaches the model instead of being dropped.
        commitPending();

        setTimeout(() => {
            if (!container.contains(document.activeElement)) {
                closeDropdown();
            }
        }, 150);
    };

    // Wire up events
    inputEl.addEventListener('input', handleInput);
    inputEl.addEventListener('keydown', handleKeyDown);
    inputEl.addEventListener('blur', handleBlur);

    // Build container
    container.appendChild(tagsContainer);
    container.appendChild(dropdown);

    // Initial render
    renderTags();

    return {
        element: container,
        getValue: () => tags,
        setValue: (value: unknown) => {
            if (Array.isArray(value)) {
                tags = value.map(String);
            } else if (typeof value === 'string') {
                tags = value.split(',').map(s => s.trim()).filter(Boolean);
            } else {
                tags = [];
            }
            tags = orderTags(tags);
            renderTags();
        },
        validate: async () => true,
        focus: () => inputEl.focus(),
        dispose: () => {
            abortController?.abort();
            inputEl.removeEventListener('input', handleInput);
            inputEl.removeEventListener('keydown', handleKeyDown);
            inputEl.removeEventListener('blur', handleBlur);
        },
    };
}
